import { Box, CircularProgress } from "@mui/material";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../../context/AuthContext";
import { FONT_MONO } from "../../theme/v2Tokens";
import {
  Transcription,
  TranscriptionReader,
  TranscriptionBlock,
} from "../../../types/transcription/Transcription";
import { useTranscriptRequest } from "../../../hooks/useTranscription";
import { useTranscribeAndSummarize } from "../../../hooks/useSummary";
import { useRedactionPolling } from "../../../hooks/useJobs";
import { JobState } from "../../../types/job/Job";
import type { RedactionOptions } from "../../../api/transcription";
import { archieButtonSx, archieIconSvg } from "./SummaryTab";
import InlineRedactionOptions from "./InlineRedactionOptions";

interface TranscriptTabProps {
  customerId: string;
  interactionId: string;
  interactionType: string;
  hasMedia: boolean;
  transcript?: Transcription;
  isLoading: boolean;
  currentTimeSeconds?: number;
}

function formatBlockTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function mapErrorMessage(
  err: Error,
  t: (key: string) => string,
): string {
  const msg = err.message || "";
  if (msg.includes("already in progress")) return t("jobAlreadyInProgress");
  if (msg.includes("403") || /permission/i.test(msg))
    return t("redactionPermissionDenied");
  return t("redactionRequestFailed");
}

export default function TranscriptTab({
  customerId,
  interactionId,
  interactionType,
  hasMedia,
  transcript,
  isLoading,
  currentTimeSeconds = 0,
}: TranscriptTabProps) {
  const { t } = useTranslation(["common", "summary"]);
  const { user } = useContext(AuthContext);
  const canRedact = user?.hasPermission("interactions:Redact") ?? false;
  const canSummarize =
    (user?.hasPermission("interactions:Replay") ||
      user?.hasPermission("interactions:ReplayRaw")) &&
    user?.hasPermission("summary-templates:Read");

  const [requestSent, setRequestSent] = useState(false);
  const [pickerMode, setPickerMode] = useState<
    null | "transcribe" | "transcribeAndSummarize" | "redactExisting"
  >(null);
  const [requestError, setRequestError] = useState<string | null>(null);

  const transcriptRequest = useTranscriptRequest();
  const transcribeAndSummarize = useTranscribeAndSummarize();

  const [redactionJobId, setRedactionJobId] = useState<string | undefined>(undefined);
  const { data: redactionJobState } = useRedactionPolling(customerId, redactionJobId);

  const redactionInProgress = redactionJobState === JobState.InProgress;
  const redactionFailed = redactionJobState === JobState.Error;

  const blocks: TranscriptionBlock[] = useMemo(() => {
    if (!transcript) return [];
    try {
      const reader = new TranscriptionReader(transcript);
      return reader.blocks;
    } catch {
      return [];
    }
  }, [transcript]);

  const canTranscribe =
    ["Audio", "Video", "AudioAndVideo"].includes(interactionType) && hasMedia;

  // Determine active block based on current playback time
  const activeBlockId = useMemo(() => {
    if (!blocks.length || currentTimeSeconds <= 0) return -1;
    for (let i = blocks.length - 1; i >= 0; i--) {
      if (blocks[i].startSeconds <= currentTimeSeconds) {
        return blocks[i].id;
      }
    }
    return -1;
  }, [blocks, currentTimeSeconds]);

  // Auto-scroll to the active block
  const activeBlockRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (activeBlockRef.current) {
      activeBlockRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [activeBlockId]);

  useEffect(() => {
    if (
      redactionJobState === JobState.Complete ||
      redactionJobState === JobState.Error
    ) {
      setRedactionJobId(undefined);
    }
  }, [redactionJobState]);

  const submitTranscribe = (redactionOptions?: RedactionOptions) => {
    setRequestError(null);
    setRequestSent(true);
    transcriptRequest.mutate(
      redactionOptions
        ? { customerId, interactionId, redactionOptions }
        : { customerId, interactionId },
      {
        onSuccess: (res) => {
          setRequestSent(false);
          if (redactionOptions?.enabled && res?.jobId) {
            setRedactionJobId(res.jobId);
          }
        },
        onError: (err) => {
          setRequestSent(false);
          setRequestError(mapErrorMessage(err, t));
        },
      },
    );
  };

  const submitTranscribeAndSummarize = (redactionOptions?: RedactionOptions) => {
    setRequestError(null);
    setRequestSent(true);
    transcribeAndSummarize.mutate(
      redactionOptions
        ? { customerId, interactionId, redactionOptions }
        : { customerId, interactionId },
      {
        onSuccess: (res) => {
          setRequestSent(false);
          if (redactionOptions?.enabled && res?.jobId) {
            setRedactionJobId(res.jobId);
          }
        },
        onError: (err) => {
          setRequestSent(false);
          setRequestError(mapErrorMessage(err, t));
        },
      },
    );
  };

  const handleTranscribe = () => {
    setRequestError(null);
    if (canRedact) {
      setPickerMode("transcribe");
      return;
    }
    submitTranscribe();
  };

  const handleTranscribeAndSummarize = () => {
    setRequestError(null);
    if (canRedact) {
      setPickerMode("transcribeAndSummarize");
      return;
    }
    submitTranscribeAndSummarize();
  };

  const handlePickerSubmit = (options: RedactionOptions) => {
    const forwarded = options.enabled ? options : undefined;
    if (pickerMode === "transcribeAndSummarize") {
      submitTranscribeAndSummarize(forwarded);
    } else {
      // "transcribe" OR "redactExisting"
      submitTranscribe(forwarded);
    }
    setPickerMode(null);
  };

  const handleRedactExisting = () => {
    setRequestError(null);
    setPickerMode("redactExisting");
  };

  const handlePickerCancel = () => {
    setPickerMode(null);
    setRedactionJobId(undefined);
  };

  const isPending =
    requestSent ||
    transcriptRequest.isPending ||
    transcribeAndSummarize.isPending;

  const canShowRedactButton =
    canTranscribe && canRedact && pickerMode === null && !isPending && !redactionInProgress;

  // Loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 120,
          gap: 1,
        }}
      >
        <CircularProgress size={16} />
        <Box
          component="span"
          sx={{ fontSize: "0.8125rem", color: "var(--text-secondary)" }}
        >
          {t("loadingTranscript")}
        </Box>
      </Box>
    );
  }

  // No transcript — show action buttons
  if (blocks.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          py: 4,
          px: 2,
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: "0.8125rem",
            color: "var(--text-tertiary)",
            mb: 1,
          }}
        >
          {t("noTranscriptAvailable")}
        </Box>

        {redactionInProgress && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: "0.8125rem",
              color: "var(--text-secondary)",
            }}
          >
            <CircularProgress size={14} />
            {t("redactionInProgress")}
          </Box>
        )}
        {redactionFailed && (
          <Box sx={{ fontSize: "0.8125rem", color: "var(--signal-rose)" }}>
            {t("redactionJobFailed")}
          </Box>
        )}
        {requestError && pickerMode === null && (
          <Box sx={{ fontSize: "0.8125rem", color: "var(--signal-rose)" }}>
            {requestError}
          </Box>
        )}

        {pickerMode !== null && (
          <Box sx={{ width: "100%", maxWidth: 420 }}>
            <InlineRedactionOptions
              variant="optional"
              isPending={isPending}
              errorMessage={requestError}
              onSubmit={handlePickerSubmit}
              onCancel={handlePickerCancel}
            />
          </Box>
        )}

        {canTranscribe && !isPending && pickerMode === null && (
          <>
            <Box
              component="button"
              onClick={handleTranscribe}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "0.8125rem",
                fontWeight: 600,
                color: "#fff",
                backgroundColor: "var(--accent)",
                border: "none",
                borderRadius: "8px",
                px: 2.5,
                py: 1,
                cursor: "pointer",
                fontFamily: "inherit",
                "&:hover": { opacity: 0.88 },
              }}
            >
              Transcribe
            </Box>
            <Box
              component="button"
              onClick={canSummarize ? handleTranscribeAndSummarize : undefined}
              disabled={!canSummarize}
              title={!canSummarize ? t("summary:noPermission") : undefined}
              sx={{
                ...archieButtonSx,
                ...(!canSummarize && {
                  opacity: 0.5,
                  cursor: "not-allowed",
                  "&:hover": { opacity: 0.5 },
                }),
              }}
            >
              {archieIconSvg}
              Transcribe & Summarize with Archie
            </Box>
          </>
        )}

        {isPending && pickerMode === null && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={14} />
            <Box
              component="span"
              sx={{ fontSize: "0.8125rem", color: "var(--text-secondary)" }}
            >
              Transcription requested...
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  // Transcript blocks
  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {redactionInProgress && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1,
            borderBottom: "1px solid var(--border-subtle)",
            fontSize: "0.8125rem",
            color: "var(--text-secondary)",
            flexShrink: 0,
          }}
        >
          <CircularProgress size={14} />
          {t("redactionInProgress")}
        </Box>
      )}
      {redactionFailed && (
        <Box
          sx={{
            px: 2,
            py: 1,
            borderBottom: "1px solid var(--border-subtle)",
            fontSize: "0.8125rem",
            color: "var(--signal-rose)",
            flexShrink: 0,
          }}
        >
          {t("redactionJobFailed")}
        </Box>
      )}
      {requestError && pickerMode === null && (
        <Box
          sx={{
            px: 2,
            py: 1,
            borderBottom: "1px solid var(--border-subtle)",
            fontSize: "0.8125rem",
            color: "var(--signal-rose)",
            flexShrink: 0,
          }}
        >
          {requestError}
        </Box>
      )}
      {canShowRedactButton && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            px: 2,
            py: 1,
            borderBottom: "1px solid var(--border-subtle)",
            flexShrink: 0,
          }}
        >
          <Box
            component="button"
            onClick={handleRedactExisting}
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "var(--text-secondary)",
              background: "none",
              border: "1px solid var(--border-mid)",
              borderRadius: "6px",
              px: 1.25,
              py: 0.5,
              cursor: "pointer",
              fontFamily: "inherit",
              "&:hover": { borderColor: "var(--text-secondary)", color: "var(--text-primary)" },
            }}
          >
            {t("redactTranscript")}
          </Box>
        </Box>
      )}

      {pickerMode !== null && (
        <Box sx={{ p: 2 }}>
          <InlineRedactionOptions
            variant={pickerMode === "redactExisting" ? "required" : "optional"}
            isPending={isPending}
            errorMessage={requestError}
            onSubmit={handlePickerSubmit}
            onCancel={handlePickerCancel}
          />
        </Box>
      )}

      <Box sx={{ py: 1 }}>
        {blocks.map((block) => {
          const isActive = block.id === activeBlockId;
          return (
            <Box
              key={block.id}
              ref={isActive ? activeBlockRef : undefined}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.25,
                px: 2,
                py: 0.875,
                borderBottom: "1px solid var(--border-subtle)",
                "&:last-child": { borderBottom: "none" },
                backgroundColor: isActive ? "var(--accent-dim)" : "transparent",
                borderRadius: isActive ? "4px" : 0,
                transition: "background-color 200ms ease",
              }}
            >
              <Box
                component="span"
                sx={{
                  fontFamily: FONT_MONO,
                  fontSize: "0.6875rem",
                  color: isActive ? "var(--accent)" : "var(--text-tertiary)",
                  flexShrink: 0,
                  minWidth: 32,
                  mt: "2px",
                }}
              >
                {formatBlockTime(block.startSeconds)}
              </Box>

              <Box
                component="span"
                sx={{
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  color:
                    block.speakerLabel === "Agent"
                      ? "var(--signal-blue)"
                      : "var(--signal-amber)",
                  flexShrink: 0,
                  minWidth: 60,
                  mt: "2px",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                {block.speakerLabel}
              </Box>

              <Box
                component="span"
                sx={{
                  fontSize: "0.8125rem",
                  color: isActive ? "var(--text-primary)" : "var(--text-secondary)",
                  lineHeight: 1.5,
                  fontWeight: isActive ? 500 : 400,
                }}
              >
                {block.text}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
